import { useEffect, useState } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { SignInButton, UserButton } from '@clerk/react';
import SignUpGateButton from './components/SignUpGateButton';
import FeedbackWidget from './components/FeedbackWidget';
import { LayoutDashboard, ShieldCheck } from 'lucide-react';
import { useAuthedApi } from './lib/useAuthedApi';
import { useClerkReady } from './lib/useClerkReady';
import OrgLanding from './pages/public/OrgLanding';
import LocationsMap from './pages/public/LocationsMap';
import LocationDetail from './pages/public/LocationDetail';
import StaffPicker from './pages/public/StaffPicker';
import BookPage from './pages/public/BookPage';
import BookingLayout from './pages/public/BookingLayout';
import AnyAvailableBook from './pages/public/AnyAvailableBook';
import ManageBooking from './pages/public/ManageBooking';
import CheckInPage from './pages/public/CheckInPage';
import AdminLayout from './pages/admin/AdminLayout';
import OrgSettings from './pages/admin/OrgSettings';
import Locations from './pages/admin/Locations';
import Services from './pages/admin/Services';
import StaffAdmin from './pages/admin/StaffAdmin';
import ApiKeys from './pages/admin/ApiKeys';
import CustomHostnames from './pages/admin/CustomHostnames';
import StaffLayout from './pages/staff/StaffLayout';
import Dashboard from './pages/staff/Dashboard';
import Profile from './pages/staff/Profile';
import Availability from './pages/staff/Availability';
import TimeOff from './pages/staff/TimeOff';
import Home from './pages/Home';
import Welcome from './pages/Welcome';
import SignInPage from './pages/SignIn';
import SignUpPage from './pages/SignUp';
import SignOutPage from './pages/SignOut';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import NotFound from './pages/NotFound';

export default function App() {
  const { pathname } = useLocation();
  const isEmbed = pathname.startsWith('/embed/') || pathname === '/embed';
  // Marketing routes get a full-bleed layout so hero sections can break out
  // of the usual page max-width. App-shell routes (admin, staff, legal) keep
  // the constrained `.page` container.
  const isMarketing = pathname === '/';
  // The dashboard is calendar-centric — give it extra width to breathe.
  const isDashboard = pathname === '/staff' || pathname === '/staff/dashboard';
  // Public booking paths (/:orgSlug/...) are org-branded via BookingLayout, so
  // the global Invitey topbar is suppressed there. Everything with a reserved
  // first segment keeps the normal chrome.
  const firstSeg = pathname.split('/')[1] ?? '';
  const RESERVED_SEGMENTS = new Set([
    '', 'welcome', 'admin', 'staff', 'sign-in', 'sign-up', 'sign-out',
    'privacy', 'terms', 'embed', 'manage', 'checkin',
  ]);
  const isBooking = !!firstSeg && !RESERVED_SEGMENTS.has(firstSeg);

  const mainClass = isEmbed
    ? 'page page-embed'
    : isMarketing
      ? 'marketing'
      : isDashboard
        ? 'page page-wide'
        : 'page';

  return (
    <>
      {!isEmbed && !isBooking && (
        <header className="topbar">
          <Link to="/" className="brand" aria-label="Invitey home">
            <img src="/invitey_logo.svg" alt="Invitey" />
          </Link>
          <div className="topbar-right">
            <TopbarAuth />
          </div>
        </header>
      )}

      <main className={mainClass}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          {/* Clerk-hosted SignIn/SignUp need their own sub-routing for
              email verification + ticket consumption, so the path is splat. */}
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />
          <Route path="/sign-out" element={<SignOutPage />} />

          {/* Admin + staff routes use nested-route syntax with <Outlet />
              rather than a /admin/* splat. The splat scores low under React
              Router's specificity ranking (admin=10, splat=-2 → 8), and
              loses to public routes like /:orgSlug/locations (3+10 = 13)
              for collisions like /admin/locations. Two static segments
              under nesting (admin + locations = 20) wins decisively. */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="settings" replace />} />
            <Route path="settings" element={<OrgSettings />} />
            <Route path="locations" element={<Locations />} />
            <Route path="services" element={<Services />} />
            <Route path="staff" element={<StaffAdmin />} />
            <Route path="api-keys" element={<ApiKeys />} />
            <Route path="hostnames" element={<CustomHostnames />} />
          </Route>
          <Route path="/staff" element={<StaffLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="profile" element={<Profile />} />
            {/* /staff/calendar + /staff/connections both fold into the
                Dashboard now (calendar connections are a card there). Keep
                both as redirects so bookmarks + the previous OAuth redirect
                target survive. */}
            <Route path="calendar" element={<Navigate to="/staff/dashboard" replace />} />
            <Route path="connections" element={<Navigate to="/staff/dashboard" replace />} />
            <Route path="availability" element={<Availability />} />
            <Route path="time-off" element={<TimeOff />} />
          </Route>

          {/* Embed routes — same components, no chrome (handled above by the
              isEmbed check). The components themselves prefix their internal
              links via useUrlPrefix(). */}
          <Route path="/embed/:orgSlug" element={<OrgLanding />} />
          <Route path="/embed/:orgSlug/locations" element={<LocationsMap />} />
          <Route path="/embed/:orgSlug/locations/:locSlug" element={<LocationDetail />} />
          <Route
            path="/embed/:orgSlug/locations/:locSlug/services/:svcSlug"
            element={<StaffPicker />}
          />
          <Route
            path="/embed/:orgSlug/locations/:locSlug/services/:svcSlug/any"
            element={<AnyAvailableBook />}
          />
          <Route path="/embed/:orgSlug/staff/:staffSlug" element={<BookPage />} />

          {/* Self-serve manage page — cancel/reschedule via signed link in
              confirmation + reminder emails. Static "manage" segment outranks
              the /:orgSlug dynamic routes, so no collision. */}
          <Route path="/manage/:token" element={<ManageBooking />} />
          <Route path="/checkin/:token" element={<CheckInPage />} />

          {/* Hosted public booking — location-first flow, wrapped in the
              org-branded BookingLayout (logo + brand-color CTAs). */}
          <Route path="/:orgSlug" element={<BookingLayout />}>
            <Route index element={<OrgLanding />} />
            <Route path="locations" element={<LocationsMap />} />
            <Route path="locations/:locSlug" element={<LocationDetail />} />
            <Route path="locations/:locSlug/services/:svcSlug" element={<StaffPicker />} />
            <Route
              path="locations/:locSlug/services/:svcSlug/any"
              element={<AnyAvailableBook />}
            />
            <Route path="staff/:staffSlug" element={<BookPage />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {/* End of <App>. <TopbarAuth> is defined below. */}

      {!isEmbed && (
        <footer className="footer">
          <div className="footer-inner">
            <Link to="/" className="brand" aria-label="Invitey home">
              <img src="/invitey_logo.svg" alt="Invitey" />
            </Link>
            <span className="muted small">
              &copy; {new Date().getFullYear()} Get Proof Digital Inc.
            </span>
            <span className="footer-links">
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
              <a href="mailto:support@invitey.ca">Contact</a>
            </span>
          </div>
        </footer>
      )}

      {/* Floating bug-report widget — self-gates to signed-in dashboard users. */}
      {!isEmbed && <FeedbackWidget />}
    </>
  );
}

/**
 * Topbar auth widget. Renders explicitly off `useAuth()` rather than Clerk's
 * `<Show>` so the loading state is observable instead of "blank topbar." If
 * Clerk fails to initialise (network, CSP, missing key) we still render the
 * Sign in / Sign up affordances by default rather than nothing.
 */
function TopbarAuth() {
  const { isLoaded, isSignedIn, orgId, orgRole, timedOut } = useClerkReady();
  const apiFn = useAuthedApi();

  // null = unknown, true = staff row exists, false = confirmed no staff row.
  // For non-admins we hide the Dashboard link in the `false` case. Admins,
  // however, always get the link (see showDashboard below): even when they're
  // not bookable, the dashboard page offers a "start taking bookings" prompt,
  // so it's a useful destination rather than a dead end.
  const [isStaff, setIsStaff] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !orgId) {
      setIsStaff(null);
      return;
    }
    let cancelled = false;
    apiFn<{ id: string }>('/staff/me')
      .then(() => {
        if (!cancelled) setIsStaff(true);
      })
      .catch((e) => {
        if (cancelled) return;
        const status = (e as Error & { status?: number }).status;
        // 404 = no staff row, 403 = no active org. Either way, no dashboard.
        if (status === 404 || status === 403) setIsStaff(false);
        // Network/5xx: leave as null so the link can still render.
      });
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, orgId, apiFn]);

  if (!isLoaded && !timedOut) {
    // Skeleton placeholder so the topbar isn't blank during Clerk init.
    return <span className="muted small">Loading…</span>;
  }

  // Clerk failed to initialise within the timeout (network/firewall/CSP/outage).
  // Fall through to the signed-out affordances rather than spinning forever, so
  // the user can retry signing in once their connection recovers.
  if (!isSignedIn) {
    return (
      <>
        <SignInButton mode="modal">
          <button type="button">Sign in</button>
        </SignInButton>
        <SignUpGateButton className="primary">Sign up</SignUpGateButton>
      </>
    );
  }

  // Only show the Admin entry to org admins. Non-admin members shouldn't even
  // see the link; the route itself is also gated in AdminLayout. Clerk's role
  // strings are namespaced like 'org:admin' / 'org:member'.
  const isAdmin = orgRole === 'org:admin';
  // Admins can always reach the dashboard (it offers the bookable opt-in when
  // they're not staff). Non-admins only see it once they have a staff row.
  const showDashboard = isAdmin || isStaff !== false;

  return (
    <>
      {isAdmin && (
        <Link to="/admin">
          <ShieldCheck size={14} /> Admin
        </Link>
      )}
      {showDashboard && (
        <Link to="/staff">
          <LayoutDashboard size={14} /> Dashboard
        </Link>
      )}
      <UserButton />
    </>
  );
}
